import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildAlarmSections } from './alarmSections';

/* 매 렌더마다 객체가 새로 만들어지지 않도록 모듈 스코프에 둔다 */
const styles = {
    overlay: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
    },
    modal: {
        background: 'var(--ho-primary)',
        borderRadius: 4,
        width: 420,
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    header: {
        padding: '12px 16px',
        height: '30px',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
    },
    headerTitle: { fontSize: 15, fontWeight: 500, margin: 0, color: 'var(--ho-bg)' },
    body: { background: '#fff', padding: '16px', flex: 1, overflowY: 'auto' },
    section: { marginBottom: 8 },
    sectionTitle: { margin: '0 0 4px', fontSize: 13, fontWeight: 500 },
    item: { marginBottom: 8 },
    itemLabel: { margin: '0 0 4px', fontSize: 13, wordBreak: 'break-all' },
    moveButton: {
        marginLeft: 4,
        padding: 0,
        border: 'none',
        background: 'none',
        color: 'blue',
        font: 'inherit',
        cursor: 'pointer',
    },
    // 여러 줄 안내문은 \n으로 넘긴다 (p 안에 p를 중첩하면 잘못된 마크업이 된다)
    itemDesc: { margin: 0, fontSize: 13, color: '#555', whiteSpace: 'pre-line' },
    footer: {
        padding: '5px 8px',
        height: '30px',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
    },
    closeButton: {
        padding: '0 20px',
        height: '100%',
        fontSize: 13,
        color: '#282828',
        border: 'none',
        borderRadius: 4,
        background: 'var(--ho-bg)',
        cursor: 'pointer',
        fontWeight: 500,
    },
};

function AlarmItem({ item, onNavigate }) {
    return (
        <div style={styles.item}>
            <p style={styles.itemLabel}>
                {item.label}
                {item.link && (
                    <button type="button" style={styles.moveButton} onClick={() => onNavigate(item.link)}>
                        [이동]
                    </button>
                )}
            </p>
            {item.desc && <p style={styles.itemDesc}>{item.desc}</p>}
        </div>
    );
}

function AlarmSection({ section, onNavigate }) {
    return (
        <div style={section.title ? styles.section : undefined}>
            {section.title && <p style={styles.sectionTitle}>{section.title}</p>}
            {section.items.map((item) => (
                <AlarmItem key={item.key} item={item} onNavigate={onNavigate} />
            ))}
        </div>
    );
}

export default function AlarmModal({ data, onClose }) {
    const navigate = useNavigate();

    const sections = useMemo(() => buildAlarmSections(data), [data]);

    // ESC로 닫기
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose?.();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const handleNavigate = useCallback((link) => {
        navigate(link);
        onClose?.();
    }, [navigate, onClose]);

    // 표출할 항목이 없으면 빈 모달을 띄우지 않는다
    if (sections.length === 0) return null;

    return (
        <div style={styles.overlay}>
            <div style={styles.modal} role="dialog" aria-modal="true" aria-labelledby="alarmModalTitle">
                <div style={styles.header}>
                    <h2 id="alarmModalTitle" style={styles.headerTitle}>
                        알람 메시지
                    </h2>
                </div>
                <div style={styles.body}>
                    {sections.map((section) => (
                        <AlarmSection key={section.key} section={section} onNavigate={handleNavigate} />
                    ))}
                </div>
                <div style={styles.footer}>
                    <button type="button" onClick={onClose} style={styles.closeButton}>
                        닫기
                    </button>
                </div>
            </div>
        </div>
    );
}
